import { type LentTo, type LentToQuery, reportingKeys } from '@taler/api-client'
import { signOfMoney } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

/** Only a row actually lent, never a net payable - owed is zero (history only) or positive. */
function excludesPayables(data: LentTo): LentTo {
  return { ...data, items: data.items.filter((item) => signOfMoney(item.owed) >= 0) }
}

export interface UseLentToOptions {
  readonly ledgerId: () => string | undefined
  readonly displayCurrency: () => LentToQuery['displayCurrency']
  readonly enabled: () => boolean
}

export interface UseLentTo {
  readonly data: ComputedRef<LentTo | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useLentTo(options: UseLentToOptions): UseLentTo {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<LentToQuery>(() => ({ displayCurrency: options.displayCurrency() }))

  const lentToQuery = useQuery({
    queryKey: computed(() => reportingKeys.lentTo(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.lentTo(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })

  return {
    data: computed(() => {
      const data = lentToQuery.data.value
      return data === undefined ? undefined : excludesPayables(data)
    }),
    isLoading: computed(() => active.value && lentToQuery.isPending.value),
    error: computed(() => lentToQuery.error.value),
    retrying: retrying(lentToQuery),
    retry: () => {
      void lentToQuery.refetch()
    },
  }
}
